import React from 'react';
import { TUTORIAL_DEFINITIONS, getTutorialDefinition } from '../tutorials';
import type { TutorialDefinition } from '../tutorials/types';
import {
  fetchTutorialProgressList,
  upsertTutorialProgress,
  type TutorialProgressRow,
} from '../services/tutorialProgressService';
import {
  TUTORIAL_HANDOFF_CHANNEL,
  consumeTutorialHandoffIgnore,
  type TutorialHandoffMessage,
} from '../utils/tutorialHandoff';
import {
  TUTORIAL_DESKTOP_ONLY_MESSAGE,
  isTutorialDesktopViewport,
} from '../utils/tutorialViewport';

type TutorialProgressMap = Record<string, TutorialProgressRow>;

type TutorialContextValue = {
  definitions: TutorialDefinition[];
  progressMap: TutorialProgressMap;
  activeTutorial: TutorialDefinition | null;
  activeStepIndex: number;
  /** true인 동안에는 목록 모달을 보여줄지 여부를 호출부에서 별도로 관리 */
  startTutorial: (tutorialId: string, opts?: { restart?: boolean; stepIndex?: number }) => void;
  exitTutorial: () => void;
  /** 해당 튜토리얼의 저장·로컬 진행 상태를 시작 전으로 되돌린다. */
  resetTutorialProgress: (tutorialId: string) => void;
  /** 단방향 진행: 실제 타깃 클릭(또는 설명 전용 단계의 버튼)으로만 다음 단계로 이동한다. */
  goToNextStep: () => void;
  /** 특정 단계 id로 점프 (건너뛰기용). 없으면 무시. */
  goToStepById: (stepId: string) => void;
};

const TutorialContext = React.createContext<TutorialContextValue | null>(null);

export function useTutorial(): TutorialContextValue {
  const ctx = React.useContext(TutorialContext);
  if (!ctx) throw new Error('useTutorial은 TutorialProvider 내부에서만 사용할 수 있습니다.');
  return ctx;
}

/** useTutorial과 달리 Provider 밖에서도 안전하게 호출 가능 (없으면 null) */
export function useTutorialOptional(): TutorialContextValue | null {
  return React.useContext(TutorialContext);
}

export const TutorialProvider: React.FC<{ workspaceId: string; children: React.ReactNode }> = ({
  workspaceId,
  children,
}) => {
  const [progressMap, setProgressMap] = React.useState<TutorialProgressMap>({});
  const [activeTutorialId, setActiveTutorialId] = React.useState<string | null>(null);
  const [activeStepIndex, setActiveStepIndex] = React.useState(0);
  const activeTutorialIdRef = React.useRef<string | null>(null);
  activeTutorialIdRef.current = activeTutorialId;
  const activeStepIndexRef = React.useRef(0);
  activeStepIndexRef.current = activeStepIndex;

  React.useEffect(() => {
    const id = workspaceId.trim();
    if (!id) {
      setProgressMap({});
      return;
    }
    let cancelled = false;
    fetchTutorialProgressList(id).then((rows) => {
      if (cancelled) return;
      const map: TutorialProgressMap = {};
      rows.forEach((row) => {
        map[row.tutorialId] = row;
      });
      setProgressMap(map);
    });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  /** 새 탭에서 같은 튜토리얼을 이어받으면 이 탭의 오버레이는 닫는다. */
  React.useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    let ch: BroadcastChannel;
    try {
      ch = new BroadcastChannel(TUTORIAL_HANDOFF_CHANNEL);
    } catch {
      return undefined;
    }
    const onMessage = (event: MessageEvent<TutorialHandoffMessage>) => {
      const data = event.data;
      if (!data || data.type !== 'tutorial-tab-handoff') return;
      const handoffId = (data.tutorialId ?? '').trim();
      if (!handoffId) return;
      // 새 탭이 스스로 보낸 신호는 무시 (BroadcastChannel은 송신 탭에도 전달됨)
      if (consumeTutorialHandoffIgnore(handoffId)) return;
      if (activeTutorialIdRef.current !== handoffId) return;
      setActiveTutorialId(null);
      setActiveStepIndex(0);
    };
    ch.addEventListener('message', onMessage);
    return () => {
      ch.removeEventListener('message', onMessage);
      ch.close();
    };
  }, []);

  const persist = React.useCallback(
    (tutorialId: string, step: number, completed: boolean) => {
      setProgressMap((prev) => ({
        ...prev,
        [tutorialId]: {
          tutorialId,
          currentStep: step,
          completed,
          completedAt: completed ? new Date().toISOString() : prev[tutorialId]?.completedAt ?? null,
          updatedAt: new Date().toISOString(),
        },
      }));
      void upsertTutorialProgress(workspaceId, tutorialId, step, completed);
    },
    [workspaceId]
  );

  const startTutorial = React.useCallback(
    (tutorialId: string, opts?: { restart?: boolean; stepIndex?: number }) => {
      const def = getTutorialDefinition(tutorialId);
      if (!def || def.steps.length === 0) return;
      if (!isTutorialDesktopViewport()) {
        window.alert(TUTORIAL_DESKTOP_ONLY_MESSAGE);
        return;
      }
      let startStep: number;
      if (typeof opts?.stepIndex === 'number') {
        startStep = Math.min(Math.max(opts.stepIndex, 0), def.steps.length - 1);
      } else {
        const existing = progressMap[tutorialId];
        startStep =
          !opts?.restart && existing && !existing.completed
            ? Math.min(Math.max(existing.currentStep, 0), def.steps.length - 1)
            : 0;
      }
      setActiveTutorialId(tutorialId);
      setActiveStepIndex(startStep);
      persist(tutorialId, startStep, false);
    },
    [progressMap, persist]
  );

  const exitTutorial = React.useCallback(() => {
    setActiveTutorialId(null);
    setActiveStepIndex(0);
  }, []);

  const resetTutorialProgress = React.useCallback(
    (tutorialId: string) => {
      const tId = tutorialId.trim();
      if (!tId) return;
      setProgressMap((prev) => {
        if (!prev[tId]) return prev;
        const next = { ...prev };
        delete next[tId];
        return next;
      });
      if (activeTutorialId === tId) {
        setActiveTutorialId(null);
        setActiveStepIndex(0);
      }
      // DB에도 시작 전 상태로 맞춤 (이어하기·완료 배지 해제)
      void upsertTutorialProgress(workspaceId, tId, 0, false);
    },
    [workspaceId, activeTutorialId]
  );

  const activeTutorial = activeTutorialId ? getTutorialDefinition(activeTutorialId) ?? null : null;
  const activeTutorialRef = React.useRef(activeTutorial);
  activeTutorialRef.current = activeTutorial;

  const goToNextStep = React.useCallback(() => {
    const tutorial = activeTutorialRef.current;
    const tutorialId = activeTutorialIdRef.current;
    if (!tutorial || !tutorialId) return;
    const next = activeStepIndexRef.current + 1;
    if (next >= tutorial.steps.length) {
      persist(tutorialId, tutorial.steps.length - 1, true);
      setActiveTutorialId(null);
      setActiveStepIndex(0);
      return;
    }
    setActiveStepIndex(next);
    persist(tutorialId, next, false);
  }, [persist]);

  const goToStepById = React.useCallback(
    (stepId: string) => {
      const tutorial = activeTutorialRef.current;
      const tutorialId = activeTutorialIdRef.current;
      if (!tutorial || !tutorialId) return;
      const idx = tutorial.steps.findIndex((s) => s.id === stepId);
      if (idx < 0) return;
      setActiveStepIndex(idx);
      persist(tutorialId, idx, false);
    },
    [persist]
  );

  const value = React.useMemo<TutorialContextValue>(
    () => ({
      definitions: TUTORIAL_DEFINITIONS,
      progressMap,
      activeTutorial,
      activeStepIndex,
      startTutorial,
      exitTutorial,
      resetTutorialProgress,
      goToNextStep,
      goToStepById,
    }),
    [
      progressMap,
      activeTutorial,
      activeStepIndex,
      startTutorial,
      exitTutorial,
      resetTutorialProgress,
      goToNextStep,
      goToStepById,
    ]
  );

  return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>;
};
